/**
 * The project repository (design 2026-09-22 §8): the register of my
 * Vorgänge, one Vorgang's record, the customer master and the consented
 * handover. Transport for `/api/v1/cases/overview`, `/cases/{id}/record`,
 * `/cases/{id}/correspondence/*`, `/cases/{id}/customer`, `/customers`,
 * `/handovers/*` and `/users`.
 *
 * Every wire type below is an alias on `components["schemas"][…]` — the
 * generated contract of Plan C, never a hand-written copy. Amounts travel as
 * decimal strings and are formatted only in `@/lib/format`; nothing in this
 * file or on any screen adds a price.
 */
import { type QueryClient, useQuery } from "@tanstack/react-query";

import { ProblemError, toProblemDetails, type components } from "@k60/contracts";

import type { CaseStatus, DocumentOut } from "./cases";
import { api } from "./client";

type S = components["schemas"];

export type CaseOverviewOut = S["CaseOverviewOut"];
export type CaseOverviewPageOut = S["CaseOverviewPageOut"];
export type VolumeOut = S["VolumeOut"];
export type CustomerBriefOut = S["CustomerBriefOut"];
export type PendingHandoverBriefOut = S["PendingHandoverBriefOut"];
export type CaseRecordOut = S["CaseRecordOut"];
export type TimelineItemOut = S["TimelineItemOut"];
export type TimelineEventOut = S["TimelineEventOut"];
export type CorrespondenceOut = S["CorrespondenceOut"];
export type AttachmentOut = S["AttachmentOut"];
export type HandoverOut = S["HandoverOut"];
export type IncomingHandoverOut = S["IncomingHandoverOut"];
export type HandoverNoticeOut = S["HandoverNoticeOut"];
export type UserOut = S["UserOut"];
export type CustomerOut = S["CustomerOut"];
export type CustomerIn = S["CustomerIn"];
export type CaseCustomerOut = S["CaseCustomerOut"];
export type ContactIn = S["ContactIn"];
export type ContactOut = S["ContactOut"];
export type CaseContactOut = S["CaseContactOut"];
export type CustomerConflictOut = S["CustomerConflictOut"];
export type CustomerProposalOut = S["CustomerProposalOut"];
export type ProposalFieldsOut = S["ProposalFieldsOut"];
export type ProposalMailOut = S["ProposalMailOut"];
export type ProposalAttachmentOut = S["ProposalAttachmentOut"];
export type CandidateOut = S["CandidateOut"];
export type ClueOut = S["ClueOut"];

export type CorrespondenceKind = CorrespondenceOut["kind"];
/** Where in the mail a value stands — the sender's header, the signature, the subject … */
export type ClueSource = ClueOut["source"];
export type LastActivityKind = CaseOverviewOut["lastActivityKind"];
export type HandoverState = HandoverOut["state"];

/**
 * Under `["cases"]` on purpose: every gesture that already invalidates
 * `CASES_KEY` (a stage tick, a new Vorgang) refreshes the register with it.
 */
export const OVERVIEW_KEY = ["cases", "overview"] as const;
/** Under `["case", id]`: a planning reload or a calculation refreshes the record too. */
export const RECORD_KEY = (caseId: string) => ["case", caseId, "record"] as const;
export const INCOMING_KEY = ["handovers", "incoming"] as const;
export const NOTICES_KEY = ["handovers", "notices"] as const;
export const USERS_KEY = ["users"] as const;
export const CUSTOMERS_KEY = (q: string) => ["customers", q] as const;
/**
 * Under `["case", id]`: an assignment invalidates the Vorgang, and the
 * proposal's candidates (their own-Vorgänge count, a customer just created
 * from this very mail) are read again with it.
 */
export const PROPOSAL_KEY = (caseId: string, entryId: string) =>
  ["case", caseId, "customer-proposal", entryId] as const;

/**
 * The sentence `RecordScreen` matches on to tell „not mine (any more)" from
 * „the server could not be reached" — a constant, like `NO_PLAN_YET`, because
 * a comparison against a copy drifts. It is also the server's own 404 detail
 * (contract §1.2, `CASE_NOT_FOUND`), deliberately the same for unknown and
 * foreign (D-1: a foreign id reveals nothing).
 */
export const RECORD_NOT_FOUND = "Diesen Vorgang gibt es nicht.";

export interface OverviewQuery {
  q?: string;
  status?: CaseStatus[];
  customerId?: string;
  includeFinished?: boolean;
  sort?: "activity" | "created" | "volume";
  limit?: number;
}

type Reply<T> = { data?: T; error?: unknown; response: Response };

/**
 * One answer or one `ProblemError`, whichever of the two shapes the reply
 * arrived in: the real client's middleware throws `ProblemError` itself on
 * every non-2xx (`api/client.ts`); a test that stubs `api.GET` hands back
 * `{ data, error, response }` instead, and this builds the same error from it
 * with the same `toProblemDetails` — so a screen only ever reads one shape.
 */
async function send<T>(request: () => Promise<Reply<T>>): Promise<T> {
  const reply = await request();
  if (!reply.response.ok || reply.data === undefined) {
    throw new ProblemError(toProblemDetails(reply.error, reply.response.status), null);
  }
  return reply.data;
}

/**
 * The query as the wire takes it — empty parts left out, so `""` and
 * `undefined` share one cache entry and one URL.
 */
function overviewParams(query: OverviewQuery) {
  const q = query.q?.trim() ?? "";
  return {
    ...(q !== "" ? { q } : {}),
    ...(query.status !== undefined && query.status.length > 0 ? { status: query.status } : {}),
    ...(query.customerId ? { customerId: query.customerId } : {}),
    ...(query.includeFinished === true ? { includeFinished: true } : {}),
    sort: query.sort ?? "activity",
    ...(query.limit !== undefined ? { limit: query.limit } : {}),
  };
}

/**
 * `GET /api/v1/cases/overview` — the register (`/vorgaenge`) and the start
 * page's preview (`limit: 5`). The previous answer stays on screen while the
 * next one loads, so a search does not blank the table on every keystroke.
 */
export function useCaseOverview(query: OverviewQuery) {
  const params = overviewParams(query);
  return useQuery({
    queryKey: [...OVERVIEW_KEY, params],
    placeholderData: (previous) => previous,
    queryFn: (): Promise<CaseOverviewPageOut> =>
      send(() => api.GET("/api/v1/cases/overview", { params: { query: params } })),
  });
}

/**
 * `GET /api/v1/cases/{id}/record` — fact sheet and timeline in one answer.
 * A 404 — unknown, foreign, or handed over a moment ago — becomes
 * `RECORD_NOT_FOUND` and is not retried.
 */
export function useCaseRecord(caseId: string | null) {
  return useQuery({
    queryKey: RECORD_KEY(caseId ?? ""),
    enabled: caseId !== null,
    retry: (count, error) => error.message !== RECORD_NOT_FOUND && count < 1,
    queryFn: () => fetchRecord(caseId!),
  });
}

/**
 * The record as the cache holds it, never asked for by this — for a page of
 * the Vorgang that is not the record (the frame on a spoke, final review
 * M-7), which reads what the record last said and adds no request of its
 * own. `undefined` until the record has been read once.
 */
export function useCachedRecord(caseId: string | null) {
  return useQuery({
    queryKey: RECORD_KEY(caseId ?? ""),
    enabled: false,
    queryFn: () => fetchRecord(caseId!),
  });
}

async function fetchRecord(caseId: string): Promise<CaseRecordOut> {
  try {
    return await send(() => api.GET("/api/v1/cases/{case_id}/record", { params: { path: { case_id: caseId } } }));
  } catch (cause) {
    if (cause instanceof ProblemError && cause.status === 404) throw new Error(RECORD_NOT_FOUND);
    throw cause;
  }
}

/**
 * The record read ahead, into `useCaseRecord`'s entry — for a row of the
 * register about to be opened (design 2026-10-24 §7). The page turn draws
 * the record's head only if its answer is there when the router draws the
 * new page, and only then do the number and title fly from the row into it;
 * otherwise they fade, and the record shows its skeleton as before. Not
 * again within 30 seconds of the last read, and not retried: a failure stays
 * in the entry, and the record asks again when it opens.
 */
export function prefetchRecord(client: QueryClient, caseId: string): void {
  void client.prefetchQuery({ queryKey: RECORD_KEY(caseId), queryFn: () => fetchRecord(caseId), staleTime: 30_000, retry: false });
}

/**
 * `GET /api/v1/handovers/incoming` — requests waiting for *me*: the start
 * page's box and the badge on „Start". Polled once a minute and on window
 * focus (TanStack's default), so a colleague's request shows up without a
 * reload — there is no e-mail notification (D-4).
 */
export function useIncomingHandovers() {
  return useQuery({
    queryKey: INCOMING_KEY,
    queryFn: (): Promise<IncomingHandoverOut[]> =>
      send(() => api.GET("/api/v1/handovers/incoming")),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/**
 * `GET /api/v1/handovers/notices` — one-off outcomes for the sender.
 *
 * `forGone`: the record reads them only once its Vorgang answers 404, and
 * then fresh (round-2 screen check, R2-3) — the one notice it looks for may
 * be seconds old, younger than the start page's copy in the cache.
 */
export function useHandoverNotices(options: { forGone?: boolean } = {}) {
  const forGone = options.forGone;
  return useQuery({
    queryKey: NOTICES_KEY,
    queryFn: (): Promise<HandoverNoticeOut[]> => send(() => api.GET("/api/v1/handovers/notices")),
    staleTime: forGone === undefined ? 30_000 : 0,
    enabled: forGone ?? true,
  });
}

/** `GET /api/v1/users` — active colleagues, without me (the server leaves me out). */
export function useUsers() {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: (): Promise<UserOut[]> => send(() => api.GET("/api/v1/users")),
    staleTime: 5 * 60_000,
  });
}

/**
 * `GET /api/v1/customers?q=` once, outside a hook — the same search the
 * field runs, asked with the address a 409 is about, to name the customer
 * who holds it (screen check S-9). The server matches name, company and
 * e-mail; no route of its own.
 */
export function searchCustomers(q: string): Promise<CustomerOut[]> {
  const term = q.trim();
  return send(() => api.GET("/api/v1/customers", { params: { query: term === "" ? {} : { q: term } } }));
}

/**
 * `GET /api/v1/customers?q=` — the shared customer master (D-3). An empty
 * search asks for the most recently changed ones (contract §1.3).
 */
export function useCustomerSearch(q: string) {
  const term = q.trim();
  return useQuery({
    queryKey: CUSTOMERS_KEY(term),
    placeholderData: (previous) => previous,
    staleTime: 30_000,
    queryFn: (): Promise<CustomerOut[]> => searchCustomers(term),
  });
}

/**
 * `GET …/correspondence/{entryId}/customer-proposal` (spec 2026-10-20 §3.1,
 * §8) — who the customer of a filed mail is, by rule, and who in the master
 * it might be. It reads and assigns nothing; the screen asks a person.
 *
 * A 404 (a mail of another Vorgang, an entry that does not exist) and a 422
 * (an entry that is no mail) are answers, not hiccups: not retried. Kept for
 * a few minutes — the server parses the original file on every request, and
 * nothing in it changes while the screen is open but the candidates, which an
 * assignment reads again through `CASE_KEY`.
 */
export function useCustomerProposal(caseId: string, entryId: string | null) {
  return useQuery({
    queryKey: PROPOSAL_KEY(caseId, entryId ?? ""),
    enabled: entryId !== null && caseId !== "",
    staleTime: 5 * 60_000,
    retry: (count, error) =>
      !(error instanceof ProblemError && (error.status === 404 || error.status === 422)) && count < 1,
    queryFn: (): Promise<CustomerProposalOut> =>
      send(() =>
        api.GET("/api/v1/cases/{case_id}/correspondence/{entry_id}/customer-proposal", {
          params: { path: { case_id: caseId, entry_id: entryId! } },
        }),
      ),
  });
}

// ── Writes ──────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * The first reason a 409 names (`ConflictOut.reasons[0]`, contract §3.5):
 * `already_pending`, `expired`, `accepted` … — or `null` where the refusal
 * names none. Screens map the code to their own sentence and fall back to
 * `ProblemError.userMessage`, the server's.
 */
export function refusalCode(error: unknown): string | null {
  if (!(error instanceof ProblemError)) return null;
  const raw = error.problem.raw;
  if (!isRecord(raw) || !Array.isArray(raw.reasons)) return null;
  const first: unknown = raw.reasons[0];
  return typeof first === "string" ? first : null;
}

/**
 * Whether a refused write says the Vorgang is not mine (any more): the owner
 * check's 404, whose sentence is `RECORD_NOT_FOUND` word for word — handed
 * over while the record was open (final review of Plan D, I-3). The record's
 * other 404s name a missing entry, attachment or customer, and are not this.
 */
export function caseGone(error: unknown): boolean {
  return error instanceof ProblemError && error.status === 404 && error.problem.detail === RECORD_NOT_FOUND;
}

/** What a form says when its write found the Vorgang gone (I-3). */
export const CASE_GONE_SENTENCE = "Der Vorgang liegt inzwischen nicht mehr bei dir.";

/**
 * The entry a correspondence 409 points at (`CorrespondenceConflictOut.existingEntryId`,
 * Plan B): „this file already lies in the record, here". `null` where the
 * refusal names none — a duplicate inside one batch, a retraction, a
 * document that exists. The intake's result block offers „Zum Eintrag" with it.
 */
export function conflictEntryId(error: unknown): string | null {
  if (!(error instanceof ProblemError)) return null;
  const raw = error.problem.raw;
  return isRecord(raw) && typeof raw.existingEntryId === "string" ? raw.existingEntryId : null;
}

const CUSTOMER_NO_TAKEN = "Diese Kundennummer gehört schon zu einem anderen Kunden";

/**
 * Thrown by `createCustomer` on a 409 (`CustomerConflictOut`, D-7): the
 * **customer number** belongs to a customer who already exists. An address
 * never refuses a write any more — several customers may share one, and
 * `customersWithEmail` is how a screen finds out before it creates another.
 * Carries that customer's id, so the form can offer „Den nehmen" instead of
 * a dead end.
 */
export class CustomerNumberTakenError extends Error {
  readonly name = "CustomerNumberTakenError";

  constructor(
    readonly existingCustomerId: string,
    detail: string,
  ) {
    super(detail);
  }
}

/** `POST /api/v1/customers` — one entry in the shared master (D-3). */
export async function createCustomer(input: CustomerIn): Promise<CustomerOut> {
  try {
    return await send(() => api.POST("/api/v1/customers", { body: input }));
  } catch (cause) {
    if (cause instanceof ProblemError && cause.status === 409) {
      const raw = cause.problem.raw;
      if (isRecord(raw) && raw.field === "customerNo" && typeof raw.existingCustomerId === "string") {
        const detail = typeof raw.detail === "string" && raw.detail !== "" ? raw.detail : CUSTOMER_NO_TAKEN;
        throw new CustomerNumberTakenError(raw.existingCustomerId, detail);
      }
    }
    throw cause;
  }
}

/**
 * `GET /api/v1/customers?email=` — exactly the customers who have this
 * address, nobody who merely resembles it (contract: `q` is ignored then).
 * The form asks before it creates, and warns instead of refusing (D-7).
 */
export function customersWithEmail(email: string): Promise<CustomerOut[]> {
  return send(() => api.GET("/api/v1/customers", { params: { query: { email: email.trim() } } }));
}

/** `PUT /api/v1/cases/{id}/customer` — assign, change, or (`null`) release. */
export async function assignCustomer(caseId: string, customerId: string | null): Promise<CaseCustomerOut> {
  return send(() =>
    api.PUT("/api/v1/cases/{case_id}/customer", {
      params: { path: { case_id: caseId } },
      body: { customerId },
    }),
  );
}

/**
 * `PUT /api/v1/cases/{id}/contact` — the person who wrote this enquiry
 * (D-8). A PUT: it replaces all three fields, and a blank one clears.
 */
export async function setCaseContact(caseId: string, contact: ContactIn): Promise<CaseContactOut> {
  return send(() =>
    api.PUT("/api/v1/cases/{case_id}/contact", {
      params: { path: { case_id: caseId } },
      body: contact,
    }),
  );
}

/**
 * `POST /api/v1/cases/{id}/correspondence` — multipart, repeated `files`,
 * optional `kind`. `FileDrop` calls this once **per file** (plan D-4), so a
 * duplicate refused with 409 does not take the other files of the same drop
 * down with it.
 */
export async function uploadCorrespondence(
  caseId: string,
  files: File[],
  kind?: "email_in" | "email_out",
): Promise<CorrespondenceOut[]> {
  const body = new FormData();
  for (const file of files) body.append("files", file, file.name);
  if (kind !== undefined) body.append("kind", kind);
  return send(() =>
    api.POST("/api/v1/cases/{case_id}/correspondence", {
      params: { path: { case_id: caseId } },
      // Same seam as `uploadDocuments`: the generated body type is JSON-shaped,
      // the wire is multipart/form-data, and this cast is where the two meet.
      body: body as never,
    }),
  );
}

/**
 * `POST …/correspondence/notes` — a note. Dated now by the server unless
 * `occurredAt` says otherwise („Telefonat gestern", UX ruling 7); the note
 * form sends it only when the person changed the time. Blank and NUL-only
 * text is refused on the server too (422, Plan B) — the form checks first.
 */
export async function addNote(caseId: string, text: string, occurredAt?: string): Promise<CorrespondenceOut> {
  return send(() =>
    api.POST("/api/v1/cases/{case_id}/correspondence/notes", {
      params: { path: { case_id: caseId } },
      body: occurredAt === undefined ? { text } : { text, occurredAt },
    }),
  );
}

/** `POST …/correspondence/{entryId}/retract` — greys an entry out with a reason; nothing is deleted. */
export async function retractEntry(caseId: string, entryId: string, reason: string): Promise<CorrespondenceOut> {
  return send(() =>
    api.POST("/api/v1/cases/{case_id}/correspondence/{entry_id}/retract", {
      params: { path: { case_id: caseId, entry_id: entryId } },
      body: { reason },
    }),
  );
}

/**
 * `POST …/attachments/{attachmentId}/adopt` — the **only** way from the
 * correspondence to the reading pipeline (design §4.3): a copy becomes a
 * `project_document`. Called from one explicit menu item, never implicitly.
 */
export async function adoptAttachment(caseId: string, entryId: string, attachmentId: string): Promise<DocumentOut> {
  return send(() =>
    api.POST("/api/v1/cases/{case_id}/correspondence/{entry_id}/attachments/{attachment_id}/adopt", {
      params: { path: { case_id: caseId, entry_id: entryId, attachment_id: attachmentId } },
    }),
  );
}

/**
 * The download address of an original (`/source`) or an attachment, for an
 * `<a download>` — the server answers `Content-Disposition: attachment` and
 * `nosniff` and `Cache-Control: no-store` (design §6.4, Plan B), so nothing a
 * customer sent is ever rendered or left behind in a shared cache.
 * Same base as every other call (`VITE_API_BASE_URL`, empty = same origin).
 */
export function correspondenceFileUrl(caseId: string, entryId: string, attachmentId?: string): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "";
  const entry = `${base}/api/v1/cases/${encodeURIComponent(caseId)}/correspondence/${encodeURIComponent(entryId)}`;
  return attachmentId === undefined ? `${entry}/source` : `${entry}/attachments/${encodeURIComponent(attachmentId)}`;
}

/** `POST /api/v1/cases/{id}/handover` — ask; the Vorgang stays mine until accepted (D-4). */
export async function requestHandover(caseId: string, toUserId: string, message: string): Promise<HandoverOut> {
  return send(() =>
    api.POST("/api/v1/cases/{case_id}/handover", {
      params: { path: { case_id: caseId } },
      body: { toUserId, message },
    }),
  );
}

export async function withdrawHandover(id: string): Promise<HandoverOut> {
  return send(() =>
    api.POST("/api/v1/handovers/{handover_id}/withdraw", { params: { path: { handover_id: id } } }),
  );
}

export async function acceptHandover(id: string): Promise<HandoverOut> {
  return send(() =>
    api.POST("/api/v1/handovers/{handover_id}/accept", { params: { path: { handover_id: id } } }),
  );
}

export async function declineHandover(id: string, reason: string | null): Promise<HandoverOut> {
  return send(() =>
    api.POST("/api/v1/handovers/{handover_id}/decline", {
      params: { path: { handover_id: id } },
      body: { reason },
    }),
  );
}

/** `POST …/notice-seen` — 204, no body; closing a notice sets `notice_seen_at`. */
export async function markNoticeSeen(id: string): Promise<void> {
  const { error, response } = await api.POST("/api/v1/handovers/{handover_id}/notice-seen", {
    params: { path: { handover_id: id } },
  });
  if (!response.ok) throw new ProblemError(toProblemDetails(error, response.status), null);
}
